import { DOCUMENT, NgIf } from '@angular/common';
import * as i0 from '@angular/core';
import { inject, signal, Component, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { EVENT_MANAGER_PLUGINS } from '@angular/platform-browser';
import { TUI_VERSION } from '@taiga-ui/cdk/constants';
import * as i3 from '@taiga-ui/cdk/directives/font-size';
import { TuiFontSize } from '@taiga-ui/cdk/directives/font-size';
import * as i1 from '@taiga-ui/cdk/directives/platform';
import { TuiPlatform } from '@taiga-ui/cdk/directives/platform';
import * as i2 from '@taiga-ui/cdk/directives/visual-viewport';
import { TuiVisualViewport } from '@taiga-ui/cdk/directives/visual-viewport';
import { tuiWatch } from '@taiga-ui/cdk/observables';
import { TUI_IS_MOBILE } from '@taiga-ui/cdk/tokens';
import { tuiInjectElement } from '@taiga-ui/cdk/utils/dom';
import { TuiAlerts } from '@taiga-ui/core/components/alert';
import { TuiDialogs } from '@taiga-ui/core/components/dialog';
import { TUI_SCROLLBAR_OPTIONS, TuiScrollControls } from '@taiga-ui/core/components/scrollbar';
import { TuiDropdowns } from '@taiga-ui/core/directives/dropdown';
import { TuiHints } from '@taiga-ui/core/directives/hint';
import { TuiPopups } from '@taiga-ui/core/directives/popup';
import { TuiBreakpointService } from '@taiga-ui/core/services';
import { TUI_REDUCED_MOTION, TUI_ANIMATIONS_SPEED, TUI_THEME } from '@taiga-ui/core/tokens';
import { tuiGetDuration } from '@taiga-ui/core/utils';
import { PreventEventPlugin } from '@taiga-ui/event-plugins';
import { map } from 'rxjs';

/// <reference types="@taiga-ui/tsconfig/ng-dev-mode" />
/// <reference types="@taiga-ui/tsconfig/ng-dev-mode" />
const _c0 = ["*", [["tuiOverContent"]], [["tuiOverDialogs"]], [["tuiOverAlerts"]], [["tuiOverDropdowns"]], [["tuiOverHints"]]];
const _c1 = ["*", "tuiOverContent", "tuiOverDialogs", "tuiOverAlerts", "tuiOverDropdowns", "tuiOverHints"];
function TuiRoot_ng_container_2_tui_scroll_controls_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "tui-scroll-controls", 3);
  }
}
function TuiRoot_ng_container_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, TuiRoot_ng_container_2_tui_scroll_controls_1_Template, 1, 0, "tui-scroll-controls", 2);
    i0.ɵɵelement(2, "tui-popups");
    i0.ɵɵprojection(3, 1);
    i0.ɵɵelement(4, "tui-dialogs");
    i0.ɵɵprojection(5, 2);
    i0.ɵɵelement(6, "tui-alerts");
    i0.ɵɵprojection(7, 3);
    i0.ɵɵelement(8, "tui-dropdowns");
    i0.ɵɵprojection(9, 4);
    i0.ɵɵelement(10, "tui-hints");
    i0.ɵɵprojection(11, 5);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", ctx_r0.scrollbars);
  }
}
let TuiRoot = /*#__PURE__*/(() => {
  class TuiRoot {
    constructor() {
      this.doc = inject(DOCUMENT);
      this.el = tuiInjectElement();
      this.reducedMotion = inject(TUI_REDUCED_MOTION);
      this.duration = tuiGetDuration(inject(TUI_ANIMATIONS_SPEED));
      this.isChildRoot = !!inject(TuiRoot, {
        optional: true,
        skipSelf: true
      });
      this.top = signal(!this.isChildRoot);
      this.isMobileRes = toSignal(inject(TuiBreakpointService).pipe(map(breakpoint => breakpoint === 'mobile'), tuiWatch()), {
        initialValue: false
      });
      this.nativeScrollbar = inject(TUI_SCROLLBAR_OPTIONS).mode === 'native';
      this.scrollbars = !this.nativeScrollbar && !inject(TUI_IS_MOBILE) && !this.isChildRoot;
      if (!this.top()) {
        return;
      }
      this.doc.documentElement.setAttribute('data-tui-theme', inject(TUI_THEME).toLowerCase());
      if (!this.nativeScrollbar) {
        this.doc.defaultView?.document.documentElement.classList.add('tui-zero-scrollbar');
      }
      ngDevMode && console.assert(!!inject(EVENT_MANAGER_PLUGINS).find(plugin => plugin instanceof PreventEventPlugin), 'NG_EVENT_PLUGINS is missing from global providers');
    }
    get isTopLayer() {
      return this.doc.fullscreenElement ? this.doc.fullscreenElement === this.el : !this.isChildRoot;
    }
    static {
      this.ɵfac = function TuiRoot_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || TuiRoot)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: TuiRoot,
        selectors: [["tui-root"]],
        hostAttrs: ["data-tui-version", "4.38.0"],
        hostVars: 6,
        hostBindings: function TuiRoot_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("touchstart.passive.zoneless", function TuiRoot_touchstart_passive_zoneless_HostBindingHandler() {
              return 0;
            })("fullscreenchange", function TuiRoot_fullscreenchange_HostBindingHandler() {
              return ctx.top.set(ctx.isTopLayer);
            }, false, i0.ɵɵresolveDocument);
          }
          if (rf & 2) {
            i0.ɵɵstyleProp("--tui-duration", ctx.duration, "ms")("--tui-scroll-behavior", ctx.reducedMotion ? "auto" : "smooth");
            i0.ɵɵclassProp("_mobile", ctx.isMobileRes());
          }
        },
        features: [i0.ɵɵHostDirectivesFeature([i1.TuiPlatform, i2.TuiVisualViewport, i3.TuiFontSize])],
        ngContentSelectors: _c1,
        decls: 3,
        vars: 1,
        consts: [[1, "t-root-content"], [4, "ngIf"], ["class", "t-root-scrollbar", 4, "ngIf"], [1, "t-root-scrollbar"]],
        template: function TuiRoot_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵprojectionDef(_c0);
            i0.ɵɵelementStart(0, "div", 0);
            i0.ɵɵprojection(1);
            i0.ɵɵelementEnd();
            i0.ɵɵtemplate(2, TuiRoot_ng_container_2_Template, 12, 1, "ng-container", 1);
          }
          if (rf & 2) {
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("ngIf", ctx.top());
          }
        },
        dependencies: [NgIf, TuiAlerts, TuiDialogs, TuiDropdowns, TuiHints, TuiPopups, TuiScrollControls],
        styles: ["@keyframes tuiPresent{to{content:\"\"}}@keyframes tuiSkeletonVibe{to{opacity:.5}}.tui-zero-scrollbar{scrollbar-width:none;-ms-overflow-style:none}.tui-zero-scrollbar::-webkit-scrollbar,.tui-zero-scrollbar::-webkit-scrollbar-thumb{display:none}body,input{margin:0}tui-root{position:relative;display:block;font:var(--tui-font-text-s);color:var(--tui-text-primary);flex:1;border-image:conic-gradient(var(--tui-background-base) 0 0) fill 0/0/0 0 100vh 0;-webkit-tap-highlight-color:transparent}tui-root>.t-root-scrollbar{position:fixed;top:0;left:0;bottom:0;right:0;z-index:0;display:none;margin:0}[data-tui-theme] tui-root>.t-root-scrollbar{display:block}.t-root-content{position:relative;top:var(--t-root-top);block-size:100%;isolation:isolate}.t-root-content>*{--t-root-top: 0}[tuiDropdownButton][tuiDropdownButton]{display:none}\n"],
        encapsulation: 2
      });
    }
  }
  return TuiRoot;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { TuiRoot };
